/* eslint-disable ember/classic-decorator-hooks */
import Route from '@ember/routing/route';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import config from '@linc-technologies/linced-common/utils/config';
import { LogLevel } from '@grafana/faro-web-sdk';

export default class SpringboardRoute extends Route {
  @service
  logging;

  @service
  session;

  @tracked
  messageListener = null;

  init() {
    super.init(...arguments);
    this.messageListener = this.handleMessage.bind(this);
  }

  beforeModel() {
    super.beforeModel(...arguments);
    if (typeof window === 'undefined') {
      return;
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    window.addEventListener('message', this.messageListener);
    this.logging.pushLog(
      'message listener attached',
      'springboard:beforeModel',
    );
    this.postToWrapper({ type: 'springboard:ready' });
    this.postToOpener({ type: 'springboard:ready' });
  }

  deactivate() {
    super.deactivate(...arguments);
    if (typeof window === 'undefined') {
      return;
    }
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
  }

  isAllowedOrigin(origin) {
    if (isEmpty(origin) || origin === 'null') {
      return false;
    }
    const trustedOrigin = config.APP?.springboardTrustedOrigin;
    if (!trustedOrigin) {
      return false;
    }
    // Use URL.hostname to strip both protocol and port, so that
    // e.g. http://localhost:4200 matches the trusted origin "localhost".
    let eventHostname;
    try {
      eventHostname = new URL(origin).hostname;
    } catch {
      return false;
    }
    return eventHostname === trustedOrigin;
  }

  async handleMessage(event) {
    try {
      if (!this.isAllowedOrigin(event.origin)) {
        this.logging.pushLog(
          `rejected message: untrusted origin: ${event.origin}`,
          'springboard:message',
          {
            level: LogLevel.WARN,
            origin: event.origin,
          },
        );
        return;
      }
      this.logging.pushLog('message received', 'springboard:message', {
        origin: event.origin || 'unknown',
        dataType: typeof event.data,
      });
      await this.session.handleSpringboardMessage(event.data);
      this.postToWrapper({ type: 'springboard:success' });
      this.replyToOpener(event, { type: 'springboard:success' });
    } catch (error) {
      this.logging.pushError(error, 'springboard:handleMessage');
      this.postToWrapper({ type: 'springboard:error' });
      this.replyToOpener(event, { type: 'springboard:error' });
    }
  }

  postToWrapper(message) {
    try {
      if (typeof window === 'undefined' || !window.FlutterBridge) {
        return;
      }
      window.FlutterBridge.postMessage(JSON.stringify(message));
      this.logging.pushLog(
        `sent message to wrapper: ${message.type}`,
        'springboard:postToWrapper',
      );
    } catch (error) {
      this.logging.pushError(error, 'springboard:postToWrapper');
    }
  }

  postToOpener(message, targetOrigin = '*') {
    try {
      if (!window.opener) {
        return;
      }
      window.opener.postMessage(message, targetOrigin);
      this.logging.pushLog(
        `sent message to opener: ${message.type}`,
        'springboard:postToOpener',
      );
    } catch (error) {
      this.logging.pushError(error, 'springboard:postToOpener');
    }
  }

  // Replies only to an opener that sent the message; the Flutter app posts from this window itself.
  replyToOpener(event, message) {
    if (event.source === window.opener) {
      this.postToOpener(message, event.origin);
    }
  }
}
